import { RSVP_VALUES, SESSION_SCOPES } from "@proef/domain";
import { z } from "zod";
import { UserProfile } from "./account.js";
import { TargetedBroadcastView } from "./broadcast.js";
import { FlightStatus } from "./flight.js";
import { PublicUser } from "./friendship.js";
import { FlightJoinCode } from "./join.js";
import { SignalEvent } from "./rtc.js";

/** Named `TastingSession*`, never a bare `Tasting` or `Session` -- pins tasting-sessions.feature's
 * naming-collision guard against tastings.ts's `Tasting`/`TastingState` and flight.ts's `Flight*`. */
export const TastingSessionStatus = z.enum(["draft", "scheduled", "live", "ended"]);
export type TastingSessionStatus = z.infer<typeof TastingSessionStatus>;

/** invited/joined are the invite-list view; online/offline are the live presence view -- both read
 * the same underlying row (design.md's "two different views over the same table" edge-visible
 * need). Realtime (a later round) is what ever moves a row to online/offline. */
export const ParticipantStatus = z.enum(["invited", "joined", "online", "offline"]);
export type ParticipantStatus = z.infer<typeof ParticipantStatus>;

/** Whether someone is coming. Asking "yes, maybe or no" is the whole of an RSVP: a person who has
 * not answered has `null`, never a fourth value. */
export const Rsvp = z.enum(RSVP_VALUES);
export type Rsvp = z.infer<typeof Rsvp>;

export const SessionScope = z.enum(SESSION_SCOPES);
export type SessionScope = z.infer<typeof SessionScope>;

/** POST /v1/sessions/:id/rsvp */
export const RsvpRequest = z.object({ rsvp: Rsvp });
export type RsvpRequest = z.infer<typeof RsvpRequest>;

/** POST /v1/sessions/:id/flight -- the host wraps a flight they host in this session. */
export const AttachSessionFlightRequest = z.object({ joinCode: FlightJoinCode });
export type AttachSessionFlightRequest = z.infer<typeof AttachSessionFlightRequest>;

export const CreateTastingSessionRequest = z.object({
  name: z.string().min(1).max(80),
  /** Made for a group: every active member who is not blocked is invited, and the creator has to be
   * one of them. */
  groupId: z.string().min(1).optional(),
  /** Single ISO-8601 UTC instant, not separate date/time strings -- timezone handling lives once,
   * server-side (design.md's DateTimeField need). */
  scheduledAt: z.iso.datetime().optional(),
});
export type CreateTastingSessionRequest = z.infer<typeof CreateTastingSessionRequest>;

/** POST /v1/sessions/:id/schedule -- draft -> scheduled, carrying the schedule time DateTimeField
 * collects (design.md: a single ISO-8601 UTC instant, never separate date/time strings). */
export const ScheduleTastingSessionRequest = z.object({
  scheduledAt: z.iso.datetime(),
});
export type ScheduleTastingSessionRequest = z.infer<typeof ScheduleTastingSessionRequest>;

/** POST /v1/sessions/:id/invite -- friends or one group as target; a group invite expands to its
 * current members at invite time (a later member-list change does not retroactively re-invite). */
export const InviteToSessionRequest = z.object({
  friendIds: z.array(z.string().min(1)).max(50).default([]),
  groupId: z.string().min(1).optional(),
});
export type InviteToSessionRequest = z.infer<typeof InviteToSessionRequest>;

export const TastingSessionParticipantView = z.object({
  userId: z.string().min(1),
  profile: UserProfile,
  status: ParticipantStatus,
  /** Their answer, `null` until they gave one. */
  rsvp: Rsvp.nullable(),
  invitedAt: z.iso.datetime(),
  joinedAt: z.iso.datetime().nullable(),
});
export type TastingSessionParticipantView = z.infer<typeof TastingSessionParticipantView>;

/** One wine slot of the wrapped flight. Which wine it is stays in the flight, so a blind flight
 * never leaks through the session; only who has submitted a note for it is shared. */
export const SessionFlightWine = z.object({
  index: z.number().int().nonnegative(),
  revealed: z.boolean(),
  /** Account participants who submitted a note for this wine. */
  submittedBy: z.array(z.string().min(1)),
});
export type SessionFlightWine = z.infer<typeof SessionFlightWine>;

/** The flight a session wraps (docs/design/social-plan.md 3, "Sessions"). */
export const SessionFlightView = z.object({
  /** Null for someone who said they are not coming: the flight is not theirs to join. */
  joinCode: FlightJoinCode.nullable(),
  status: FlightStatus,
  blind: z.boolean(),
  currentIndex: z.number().int().nonnegative(),
  /** Whether the viewer's account is taking part in the flight yet. */
  joined: z.boolean(),
  wines: z.array(SessionFlightWine),
});
export type SessionFlightView = z.infer<typeof SessionFlightView>;

export const TastingSessionView = z.object({
  id: z.string().min(1),
  name: z.string(),
  status: TastingSessionStatus,
  hostUserId: z.string().min(1),
  scheduledAt: z.iso.datetime().nullable(),
  startedAt: z.iso.datetime().nullable(),
  endedAt: z.iso.datetime().nullable(),
  isHost: z.boolean(),
  /** The group it was planned for, if any. */
  groupId: z.string().min(1).nullable(),
  /** The viewer's own answer. */
  rsvp: Rsvp.nullable(),
  /** The flight it wraps, once the host attached one. */
  flight: SessionFlightView.nullable(),
  /** Never null on an empty session -- an empty array, so the contract has one shape for "zero
   * participants" (tasting-sessions.feature's empty-session scenario). */
  participants: z.array(TastingSessionParticipantView),
  createdAt: z.iso.datetime(),
  /** Present (true) only when the clock ended it for want of a start; its host can plan a new time. */
  lapsed: z.literal(true).exactOptional(),
});
export type TastingSessionView = z.infer<typeof TastingSessionView>;

/** One row of the hub's lists. */
export const SessionSummary = z.object({
  id: z.string().min(1),
  name: z.string(),
  status: TastingSessionStatus,
  scheduledAt: z.iso.datetime().nullable(),
  startedAt: z.iso.datetime().nullable(),
  endedAt: z.iso.datetime().nullable(),
  /** Ended without ever going live: the host called it off. */
  cancelled: z.boolean(),
  isHost: z.boolean(),
  host: PublicUser,
  groupId: z.string().min(1).nullable(),
  /** The viewer's own answer. */
  rsvp: Rsvp.nullable(),
  participantCount: z.int().nonnegative(),
  /** Participants who said yes, the host counted. */
  goingCount: z.int().nonnegative(),
  hasFlight: z.boolean(),
  /** Present (true) only when the clock ended it for want of a start; its host can plan a new time. */
  lapsed: z.literal(true).exactOptional(),
});
export type SessionSummary = z.infer<typeof SessionSummary>;

/** The most rows one page of the hub's list holds. */
export const SESSION_PAGE_MAX = 50;

/** GET /v1/sessions's query string; `limit` arrives as text, so it is coerced. */
export const SessionListQuery = z.object({
  scope: SessionScope,
  cursor: z.string().min(1).max(400).optional(),
  limit: z.coerce.number().int().min(1).max(SESSION_PAGE_MAX).default(20),
});
export type SessionListQuery = z.infer<typeof SessionListQuery>;

/** GET /v1/sessions?scope=&cursor= */
export const SessionList = z.object({
  items: z.array(SessionSummary),
  nextCursor: z.string().nullable(),
});
export type SessionList = z.infer<typeof SessionList>;

/** The DO's join snapshot -- session status + presence in one message, so a joiner never renders a
 * partial picture before the first live event (realtime-room.feature's "snapshot before any live
 * event" scenario). */
export const SessionRoomSnapshot = z.object({
  session: TastingSessionView,
  onlineUserIds: z.array(z.string().min(1)),
});
export type SessionRoomSnapshot = z.infer<typeof SessionRoomSnapshot>;

/** The room's own wire frames: a snapshot on connect, an "ended" notice when the host ends the
 * session, and a "broadcast" frame (broadcasts.feature's online fan-out path) delivered only to the
 * resolved targets who have a live socket right now -- an offline target never sees this frame, it
 * gets the existing push sender instead (apps/edge/src/broadcasts.ts). */
export const SessionRoomEvent = z.discriminatedUnion("kind", [
  SessionRoomSnapshot.extend({ kind: z.literal("snapshot") }),
  z.object({ kind: z.literal("ended") }),
  TargetedBroadcastView.extend({ kind: z.literal("broadcast") }),
  // WebRTC signalling shares the room socket, so peers reach each other without a second one.
  ...SignalEvent.options,
]);
export type SessionRoomEvent = z.infer<typeof SessionRoomEvent>;
